import { Phone, MessageSquare, Mail, Clock } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";
import { SITE } from "@/lib/site";

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact CYAN Wireless — Call, Text or Email for Rural Internet"
        description="Get in touch with CYAN Wireless for fast, reliable rural internet. Call or text (970) 440-3178, email support@cyanwireless.net, or send us a message — friendly local support in Colorado."
        path="/contact"
      />
      <section className="relative pt-40 pb-24 sm:pt-48 bg-ink-950 overflow-hidden">
        <div className="absolute top-24 -right-40 w-[500px] h-[500px] rounded-full bg-cyan-500/10 blur-[140px]" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
          {/* Left */}
          <Reveal>
            <p className="overline mb-6 flex items-center gap-3"><span className="w-8 h-px bg-cyan-500" />Let's talk</p>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-white leading-[0.9]">
              Real people.<br />Local <span className="text-cyan-400 text-glow">support.</span>
            </h1>
            <p className="mt-6 text-lg text-zinc-400 max-w-md">
              Looking for fast, reliable internet? We're available by phone, text or email — and if we miss you, we call right back.
            </p>

            <a href={`tel:${SITE.phoneRaw}`} className="group mt-10 block" data-testid="contact-phone-big">
              <span className="font-mono-tech text-xs uppercase tracking-widest text-cyan-400">Call or text</span>
              <span className="block font-display font-black text-4xl sm:text-5xl text-white group-hover:text-cyan-400 transition-colors tracking-tighter">{SITE.phone}</span>
            </a>

            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              <a href={`tel:${SITE.phoneRaw}`} className="flex items-center gap-3 bg-ink-900 border border-white/10 rounded-xl p-4 hover:border-cyan-500/50 transition-colors">
                <Phone className="text-cyan-400" size={18} /> <span className="text-white text-sm">Call us</span>
              </a>
              <a href={`sms:${SITE.phoneRaw}`} className="flex items-center gap-3 bg-ink-900 border border-white/10 rounded-xl p-4 hover:border-cyan-500/50 transition-colors">
                <MessageSquare className="text-cyan-400" size={18} /> <span className="text-white text-sm">Text us</span>
              </a>
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 bg-ink-900 border border-white/10 rounded-xl p-4 hover:border-cyan-500/50 transition-colors sm:col-span-2">
                <Mail className="text-cyan-400" size={18} /> <span className="text-white text-sm">{SITE.email}</span>
              </a>
            </div>

            <div className="mt-8 bg-ink-900 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3 text-cyan-400"><Clock size={16} /><span className="font-mono-tech text-xs uppercase tracking-widest">Hours</span></div>
              <ul className="space-y-1.5 text-sm text-zinc-400">
                {SITE.hours.map((h) => (
                  <li key={h.d} className="flex justify-between"><span>{h.d}</span><span className="text-zinc-500">{h.h}</span></li>
                ))}
              </ul>
            </div>
          </Reveal>

          {/* Right form */}
          <Reveal delay={0.1} className="bg-ink-900 border border-white/10 rounded-2xl p-8 self-start">
            <h2 className="font-display font-bold text-2xl text-white mb-2">Send a message</h2>
            <p className="text-zinc-400 text-sm mb-6">We'll get right back to you.</p>
            <LeadForm type="contact" submitLabel="Send Message" showAddress={false} />
          </Reveal>
        </div>
      </section>
    </>
  );
}
